
import { mapState, mapMutations } from 'vuex'

export default {
  data() {
    return {
      openIndex: null, // which top-level submenu is open
      lastFocusTop: null, // last focused top-level index
    }
  },
  computed: {
    ...mapState('navigation', ['isOpen', 'pages']),
  },
  watch: {
    isOpen() {
      this.syncOpenToRoute()
    },
    '$route.path'() {
      // keep submenu in sync with current route
      this.syncOpenToRoute()
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick, true)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick, true)
  },
  methods: {
    ...mapMutations('navigation', ['toggleNav', 'closeNav']),

    submenuId(i) {
      return `submenu-${i}`
    },

    openOnHover(i) {
      // optional: only open on hover if a user uses mouse
      this.openIndex = i
    },

    openOnFocus(i) {
      this.lastFocusTop = i
      this.openIndex = i
    },

    toggleSubmenu(i) {
      this.openIndex = this.openIndex === i ? null : i
      this.$nextTick(() => {
        if (this.openIndex === i) this.focusFirstSub(i)
      })
    },

    closeAll() {
      this.openIndex = null
      this.closeNav()
    },

    syncOpenToRoute() {
      if (this.isOpen === false) {
        this.openIndex = null
        return
      }
      const current = this.pages.findIndex(
        (page) =>
          page.children && page.children.some((c) => c.url === this.$route.path)
      )
      this.openIndex = current >= 0 ? current : null
    },

    // ---------- Keyboard handlers: TOP LEVEL ----------
    onTopKeydown(e, i) {
      const count = this.pages.length
      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault()
          this.moveFocusTop((i + 1) % count)
          break
        case 'ArrowLeft':
          e.preventDefault()
          this.moveFocusTop((i - 1 + count) % count)
          break
        case 'Enter':
        case ' ':
          e.preventDefault()
          this.toggleSubmenu(i)
          break
        case 'ArrowDown':
          e.preventDefault()
          if (this.openIndex !== i) this.openIndex = i
          this.$nextTick(() => this.focusFirstSub(i))
          break
        case 'ArrowUp':
          e.preventDefault()
          if (this.openIndex !== i) this.openIndex = i
          this.$nextTick(() => this.focusLastSub(i))
          break
        case 'Escape':
          e.preventDefault()
          if (this.openIndex === null) this.$store.commit('navigation/closeNav')
          this.openIndex = null
          this.focusTop(i)
          break
      }
    },

    // ---------- Keyboard handlers: SUBMENU ----------
    onSubKeydown(e, i) {
      const subs = this.getSubLinks(i)
      if (!subs.length) return
      const currentIndex = subs.indexOf(document.activeElement)
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          this.focusSub(i, (currentIndex + 1) % subs.length)
          break
        case 'ArrowUp':
          e.preventDefault()
          this.focusSub(i, (currentIndex - 1 + subs.length) % subs.length)
          break
        case 'Home':
          e.preventDefault()
          this.focusSub(i, 0)
          break
        case 'End':
          e.preventDefault()
          this.focusSub(i, subs.length - 1)
          break
        case 'Escape':
          e.preventDefault()
          this.openIndex = null
          this.focusTop(i)
          break
        case 'ArrowRight': {
          e.preventDefault()
          const nextTop = this.nextTopIndex(i)
          this.openIndex = nextTop
          this.$nextTick(() => this.focusFirstSub(nextTop))
          break
        }
        case 'ArrowLeft': {
          e.preventDefault()
          const prevTop = this.prevTopIndex(i)
          this.openIndex = prevTop
          this.$nextTick(() => this.focusFirstSub(prevTop))
          break
        }
      }
    },

    // ---------- Focus helpers ----------
    moveFocusTop(i) {
      this.focusTop(i)
      this.openIndex = i
    },
    focusTop(i) {
      const btns = this.$refs.topButtons || []
      const el = Array.isArray(btns) ? btns[i] : null
      if (el && el.focus) el.focus()
    },
    getSubLinks(i) {
      const container = document.getElementById(this.submenuId(i))
      const raw = Array.isArray(this.$refs.subLinks) ? this.$refs.subLinks : []
      const domNodes = raw.map(this.toDom).filter(Boolean)
      return domNodes.filter((n) => container && container.contains(n))
    },
    focusFirstSub(i) {
      const subs = this.getSubLinks(i)
      if (subs[0] && subs[0].focus) subs[0].focus()
    },
    focusLastSub(i) {
      const subs = this.getSubLinks(i)
      const el = subs[subs.length - 1]
      if (el && el.focus) el.focus()
    },
    focusSub(i, j) {
      const subs = this.getSubLinks(i)
      const el = subs[j]
      if (el && el.focus) el.focus()
    },
    nextTopIndex(i) {
      return (i + 1) % this.pages.length
    },
    prevTopIndex(i) {
      return (i - 1 + this.pages.length) % this.pages.length
    },
    toDom(el) {
      // If it's a Vue component (like <nuxt-link>), use its $el
      return el && el.$el ? el.$el : el
    },

    // ---------- Click outside / blur management ----------
    onDocumentClick(e) {
      // close if click happens outside the nav
      const nav = this.$el
      if (nav && !nav.contains(e.target)) {
        this.openIndex = null
      }
    },
  },
}
